import React, { useState, useEffect, Suspense, lazy } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './components/Home';
import { CATEGORIES, SERVED_CITIES } from './constants';
import { ImageProvider } from './contexts/ImageContext';
import { BlogProvider } from './contexts/BlogContext';
import { ArrowUp, Loader2 } from 'lucide-react';

// Lazy Load Pages to reduce initial bundle size (Code Splitting)
const ShopLanding = lazy(() => import('./components/ShopLanding'));
const CollectionPage = lazy(() => import('./components/CollectionPage'));
const DonationsPage = lazy(() => import('./components/DonationsPage'));
const CityDonationPage = lazy(() => import('./components/CityDonationPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const GalleryPage = lazy(() => import('./components/GalleryPage'));
const VisitPage = lazy(() => import('./components/VisitPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const NewsPage = lazy(() => import('./components/NewsPage'));
const ServicesPage = lazy(() => import('./components/ServicesPage'));
const CityLandingPage = lazy(() => import('./components/CityLandingPage'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh] bg-stone-50">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
      <p className="text-slate-500 font-serif italic">Loading...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  const [view, setView] = useState<string>(() => {
    try {
      const path = window.location.pathname.replace(/^\/+/, '');
      return path || 'home';
    } catch (e) {
      return 'home';
    }
  });
  const [shopInitialForm, setShopInitialForm] = useState<'inquiry' | 'sell'>('inquiry');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      try {
        const path = window.location.pathname.replace(/^\/+/, '');
        setView(path || 'home');
      } catch (e) {
        setView('home');
      }
    };
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleNavigate = (newView: string) => {
    const cleanView = newView.replace(/^\/+/, '');
    setView(cleanView || 'home');
    
    // Safely update history. In sandboxed environments like AI Studio (blob origin)
    // or specific usercontent subdomains, pushState is restricted and will throw a SecurityError.
    try {
      const isSandboxed = 
        window.location.protocol === 'blob:' || 
        window.location.hostname.includes('usercontent.goog') ||
        window.location.hostname.includes('ai.studio') ||
        window.self !== window.top;

      if (!isSandboxed) {
        const url = cleanView === 'home' || cleanView === '' ? '/' : `/${cleanView}`;
        if (window.location.pathname !== url) {
          window.history.pushState({}, '', url);
        }
      }
    } catch (e) {
      // Fail silently in restricted environments to keep the app functional
      console.warn('Navigation: history manipulation is restricted in this context.');
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToShop = (formType: 'inquiry' | 'sell' = 'inquiry') => {
    setShopInitialForm(formType);
    handleNavigate('shop');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = () => {
    switch (view) {
      case 'home': return <Home onNavigate={handleNavigate} onShop={navigateToShop} />;
      case 'shop': return <ShopLanding initialFormType={shopInitialForm} />;
      case 'services': return <ServicesPage onNavigate={handleNavigate} onShop={navigateToShop} />;
      case 'donations': return <DonationsPage onNavigate={handleNavigate} />;
      case 'about': return <AboutPage />;
      case 'gallery': return <GalleryPage />;
      case 'news': return <NewsPage />;
      case 'visit': return <VisitPage onNavigate={handleNavigate} />;
      case 'admin': return <AdminPage />;
    }

    if (view.startsWith('donations-')) {
      const cityId = view.replace('donations-', '');
      const city = SERVED_CITIES.find(c => c.id === cityId);
      if (city) return <CityDonationPage city={city} onNavigate={handleNavigate} />;
    }

    const category = CATEGORIES.find(c => c.id === view);
    if (category) return <CollectionPage category={category} onBack={() => handleNavigate('home')} onShop={() => navigateToShop('inquiry')} />;

    const city = SERVED_CITIES.find(c => c.id === view);
    if (city) return <CityLandingPage city={city} onNavigate={handleNavigate} />;

    return <Home onNavigate={handleNavigate} onShop={navigateToShop} />;
  };

  return (
    <ImageProvider>
      <BlogProvider>
        <div className="min-h-screen flex flex-col font-sans bg-stone-50">
          <Header onNavigate={handleNavigate} currentView={view} />
          <main className="flex-grow">
            <Suspense fallback={<PageLoader />}>
              {renderContent()}
            </Suspense>
          </main>
          <Footer onNavigate={handleNavigate} />
          
          <button
            onClick={scrollToTop}
            className={`fixed bottom-6 right-6 z-40 p-3 bg-brand-primary text-brand-dark rounded-full shadow-2xl transition-all duration-300 transform ${
              showBackToTop ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-20 opacity-0 scale-50 pointer-events-none'
            } md:hover:scale-110`}
            aria-label="Back to top"
          >
            <ArrowUp className="w-6 h-6" />
          </button>
        </div>
      </BlogProvider>
    </ImageProvider>
  );
};

export default App;